import React, { useState } from "react";
import { FieldArray, FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { UserFormValues } from "src/models/User.model";
import { handleValidNumber } from "src/utils/validations/numberInput";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import ATMTextArea from "src/components/UI/atoms/formFields/ATMTextArea/ATMTextArea";
import moment from "moment";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMPassword from "src/components/UI/atoms/formFields/ATMPassword/ATMPassword";
import { GrFormAdd } from "react-icons/gr";
import { MdDelete } from "react-icons/md";

type Props = {
  formikProps: FormikProps<UserFormValues>;
  onClose: () => void;
  formType: "ADD" | "EDIT";
};

const UserForm = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  const genderOption = [
    { label: "Male", value: "MALE" },
    { label: "Female", value: "FEMALE" },
    { label: "Other", value: "OTHER" },
  ];

  const [activeSection, setActiveSection] = useState<
    "personal" | "banking" | "address"
  >("personal");

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add User" : "Update User"}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="flex gap-4">
        <button
          type="button"
          className={`px-4 py-2 rounded ${
            activeSection === "personal"
              ? "bg-gray-800 text-white"
              : "bg-gray-200 text-black"
          }`}
          onClick={() => setActiveSection("personal")}
        >
          Personal Information
        </button>
        <button
          type="button"
          className={`px-4 py-2 rounded ${
            activeSection === "banking"
              ? "bg-gray-800 text-white"
              : "bg-gray-200 text-black"
          }`}
          onClick={() => setActiveSection("banking")}
        >
          Banking Information
        </button>
        <button
          type="button"
          className={`px-4 py-2 rounded ${
            activeSection === "address"
              ? "bg-gray-800 text-white"
              : "bg-gray-200 text-black"
          }`}
          onClick={() => setActiveSection("address")}
        >
          Address Information
        </button>
      </div>

      {/* Form Sections */}
      {activeSection === "personal" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ATMTextField
            name="firstName"
            value={values.firstName}
            onChange={(e) => setFieldValue("firstName", e.target.value)}
            label="First Name"
            placeholder="Enter First name"
            onBlur={handleBlur}
          />
          <ATMTextField
            name="lastName"
            value={values.lastName}
            onChange={(e) => setFieldValue("lastName", e.target.value)}
            label="Last Name"
            placeholder="Enter Last name"
            onBlur={handleBlur}
          />
          <ATMTextField
            required
            name="email"
            value={values.email}
            onChange={(e) => setFieldValue("email", e.target.value)}
            label="Email"
            placeholder="Enter Email"
            onBlur={handleBlur}
          />
          <ATMSelect
            required
            name="gender"
            value={values.gender}
            options={genderOption}
            onChange={(value) => setFieldValue("gender", value)}
            label="Gender"
            placeholder="Select Gender"
            onBlur={handleBlur}
          />
          <ATMDatePicker
            required
            name="dob"
            value={values.dob || null}
            format="DD/MM/YYYY"
            onChange={(newValue) =>
              setFieldValue("dob", moment(newValue).format("YYYY-MM-DD"))
            }
            label="Date of Birth"
          />
          <ATMTextField
            required
            name="mobile"
            value={values.mobile}
            onChange={(e) =>
              handleValidNumber(e) && setFieldValue("mobile", e.target.value)
            }
            label="Mobile"
            placeholder="Enter Mobile"
            onBlur={handleBlur}
          />
          <ATMTextField
            required
            name="altMobile"
            value={values.altMobile}
            onChange={(e) =>
              handleValidNumber(e) && setFieldValue("altMobile", e.target.value)
            }
            label="Alternative Mobile"
            placeholder="Enter Alternative Mobile"
            onBlur={handleBlur}
          />
          {formType === "ADD" && (
            <ATMPassword
              name="password"
              value={values.password}
              onChange={(e) => setFieldValue("password", e.target.value)}
              label="Password"
              required
              placeholder="Enter Password"
              onBlur={handleBlur}
            />
          )}
          <ATMTextField
            name="upiId"
            value={values.upiId}
            onChange={(e) => setFieldValue("upiId", e.target.value)}
            label="UPI Id"
            placeholder="Enter UPI Id"
            onBlur={handleBlur}
          />
          <ATMTextField
            name="subjects"
            value={values.subjects}
            onChange={(e) => setFieldValue("subjects", e.target.value)}
            label="Subject"
            placeholder="Enter Subject Name"
            onBlur={handleBlur}
          />
          <ATMTextField
            name="walletAmount"
            value={values.walletAmount}
            onChange={(e) =>
              handleValidNumber(e) &&
              setFieldValue("walletAmount", e.target.value)
            }
            label="Wallet Amount"
            placeholder="Enter Wallet Amount"
            onBlur={handleBlur}
          />
          <ATMTextField
            name="course"
            value={values.course}
            onChange={(e) => setFieldValue("course", e.target.value)}
            label="Course"
            placeholder="Enter Course"
            onBlur={handleBlur}
          />
          <ATMTextField
            name="level"
            value={values.level}
            onChange={(e) => setFieldValue("level", e.target.value)}
            label="Level"
            placeholder="Enter Level"
            onBlur={handleBlur}
          />
          <ATMTextField
            name="referralCode"
            value={values.referralCode}
            onChange={(e) => setFieldValue("referralCode", e.target.value)}
            label="Referral Code"
            placeholder="Enter Referral Code"
            onBlur={handleBlur}
          />
          <ATMTextField
            name="referralBy"
            value={values.referralBy}
            onChange={(e) => setFieldValue("referralBy", e.target.value)}
            label="Referral By"
            placeholder="Enter Referral By"
            onBlur={handleBlur}
          />
        </div>
      )}

      {/* Banking Information */}
      {activeSection === "banking" && (
        <FieldArray name="bankAccountDetails">
          {({ push, remove }) => (
            <div className="flex flex-col">
              {values?.bankAccountDetails?.map((sbs: any, index: any) => (
                <>
                  <div className="flex justify-end">
                    {/* Delete Button */}
                    <button
                      type="button"
                      className={`'} text-red-700 flex justify-center items-center `}
                      onClick={() => remove(index)}
                    >
                      <MdDelete size={20} />
                    </button>
                  </div>
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <div>
                      <ATMTextField
                        name={`bankAccountDetails[${index}].accountHolderName`}
                        placeholder="Enter Account Holder Name"
                        label="Bank Account Holder Name"
                        onChange={(newValue: any) => {
                          setFieldValue(
                            `bankAccountDetails[${index}].accountHolderName`,
                            newValue.target.value
                          );
                        }}
                        value={
                          values.bankAccountDetails[index].accountHolderName
                        }
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`bankAccountDetails[${index}].bankAccountNumber`}
                        value={
                          values.bankAccountDetails[index].bankAccountNumber
                        }
                        onChange={(e) =>
                          setFieldValue(
                            `bankAccountDetails[${index}].bankAccountNumber`,
                            e.target.value
                          )
                        }
                        label="Bank Account Number"
                        placeholder="Enter Account Holder Name"
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`bankAccountDetails[${index}].ifscCode`}
                        value={values.bankAccountDetails[index].ifscCode}
                        onChange={(e) =>
                          setFieldValue(
                            `bankAccountDetails[${index}].ifscCode`,
                            e.target.value
                          )
                        }
                        label="IFSC Code"
                        placeholder="Enter IFSC Code"
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`bankAccountDetails[${index}].bankName`}
                        value={values.bankAccountDetails[index].bankName}
                        onChange={(e) =>
                          setFieldValue(
                            `bankAccountDetails[${index}].bankName`,
                            e.target.value
                          )
                        }
                        label="Bank Name"
                        placeholder="Enter Bank Name"
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`bankAccountDetails[${index}].branchName`}
                        value={values.bankAccountDetails[index].branchName}
                        onChange={(e) =>
                          setFieldValue(
                            `bankAccountDetails[${index}].branchName`,
                            e.target.value
                          )
                        }
                        label="Branch Name"
                        placeholder="Enter Branch Name"
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>
                </>
              ))}

              {/* Add More Button */}
              <button
                type="button"
                onClick={() =>
                  push({
                    accountHolderName: "",
                    bankAccountNumber: "",
                    ifscCode: "",
                    bankName: "",
                    branchName: "",
                  })
                }
                className="rounded border-dashed border border-slate-500  font-medium text-md mt-[15px]  text-gray-400 flex justify-center items-center gap-[2px] "
              >
                Add{" "}
                {values?.bankAccountDetails?.length
                  ? "More"
                  : "Bank Account Details"}{" "}
                <span>
                  <GrFormAdd
                    color="#ADB8C8"
                    size="1.5em"
                    className="font-semibold"
                  />
                </span>
              </button>
            </div>
          )}
        </FieldArray>
      )}

      {/* Address Information */}
      {activeSection === "address" && (
        <FieldArray name="address">
          {({ push, remove }) => (
            <div className="flex flex-col ">
              {values?.address?.map((abc: any, index: any) => (
                <>
                  <div className="flex justify-end">
                    {/* Delete Button */}
                    <button
                      type="button"
                      className={`'}  text-red-700  flex justify-center items-center `}
                      onClick={() => remove(index)}
                    >
                      <MdDelete size={20} />
                    </button>
                  </div>
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <div>
                      <ATMTextField
                        name={`address[${index}].country`}
                        placeholder="Enter Country Name"
                        label="Country"
                        onChange={(newValue: any) => {
                          setFieldValue(
                            `address[${index}].country`,
                            newValue.target.value
                          );
                        }}
                        value={values.address[index].country}
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`address[${index}].state`}
                        value={values.address[index].state}
                        onChange={(e) =>
                          setFieldValue(
                            `address[${index}].state`,
                            e.target.value
                          )
                        }
                        label="State"
                        placeholder="Enter State Name"
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`address[${index}].city`}
                        value={values.address[index].city}
                        onChange={(e) =>
                          setFieldValue(
                            `address[${index}].city`,
                            e.target.value
                          )
                        }
                        label="City"
                        placeholder="Enter City Name"
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`address[${index}].pincode`}
                        value={values.address[index].pincode}
                        onChange={(e) =>
                          setFieldValue(
                            `address[${index}].pincode`,
                            e.target.value
                          )
                        }
                        label="Pincode"
                        placeholder="Enter Pincode"
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`address[${index}].landmark`}
                        value={values.address[index].landmark}
                        onChange={(e) =>
                          setFieldValue(
                            `address[${index}].landmark`,
                            e.target.value
                          )
                        }
                        label="Landmark"
                        placeholder="Enter Landmark"
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="">
                      <ATMTextField
                        name={`address[${index}].addressType`}
                        value={values.address[index].addressType}
                        onChange={(e) =>
                          setFieldValue(
                            `address[${index}].addressType`,
                            e.target.value
                          )
                        }
                        label="Address Type"
                        placeholder="Enter Address Type"
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className="">
                      <ATMTextArea
                        name={`address[${index}].addressLine`}
                        value={values.address[index].addressLine}
                        onChange={(value) =>
                          setFieldValue(`address[${index}].addressLine`, value)
                        }
                        label="Address"
                        placeholder="Enter Address"
                      />
                    </div>
                  </div>
                </>
              ))}

              {/* Add More Button */}
              <button
                type="button"
                onClick={() =>
                  push({
                    country: "",
                    state: "",
                    city: "",
                    pincode: "",
                    addressLine: "",
                    landmark: "",
                    addressType: "",
                  })
                }
                className="rounded border-dashed border border-slate-500  font-medium text-md mt-[15px]  text-gray-400 flex justify-center items-center gap-[2px] "
              >
                Add {values?.address?.length ? "More" : "Address"}{" "}
                <span>
                  <GrFormAdd
                    color="#ADB8C8"
                    size="1.5em"
                    className="font-semibold"
                  />
                </span>
              </button>
            </div>
          )}
        </FieldArray>
      )}

      {/* Submit Button */}
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default UserForm;
